import { z } from "zod";

import { ACHIEVEMENT_IDS, CONTRACT_TYPES, GRADES, SKILL_IDS, TOOL_IDS } from "../types/enums";
import { SAVE_VERSION } from "../types/player";

const nonNegativeInt = z.number().int().min(0);
const scoreInt = z.number().int().min(0).max(100);

const resultHistoryEntrySchema = z
  .object({
    contractId: z.string().min(1),
    grade: z.enum(GRADES),
    score: nonNegativeInt,
    money: nonNegativeInt,
    xp: nonNegativeInt,
    at: nonNegativeInt,
    taskScores: z.array(scoreInt),
    taskTypes: z.array(z.enum(CONTRACT_TYPES)).optional(),
  })
  .strict();

const playerSchema = z
  .object({
    id: z.string().min(1),
    name: z.string(),
    level: z.number().int().min(1),
    xp: nonNegativeInt,
    money: nonNegativeInt,
    reputation: nonNegativeInt,
    skillPoints: nonNegativeInt,
    skills: z.record(z.enum(SKILL_IDS), nonNegativeInt),
    ownedTools: z.array(z.enum(TOOL_IDS)),
    completedContracts: z.array(z.string().min(1)),
    achievements: z.array(z.enum(ACHIEVEMENT_IDS)),
  })
  .strict();

const agencySchema = z.object({ name: z.string() }).strict();

const trainingSchema = z.object({ bought: nonNegativeInt }).strict();

const streakSchema = z
  .object({
    current: nonNegativeInt,
    longest: nonNegativeInt,
    lastClaimDate: z.string(),
  })
  .strict();

const urgentOfferSchema = z
  .object({
    contractId: z.string().min(1),
    deadlineMs: z.number().int().nonnegative(),
    bonusPct: z.number().min(0).max(1),
    durationMs: z.number().int().positive(),
  })
  .strict();

const offerBoardSchema = z
  .object({
    available: z.array(z.string().min(1)),
    urgent: urgentOfferSchema.nullable(),
    nextUrgentAtMs: z.number().int().nonnegative().nullable().optional(),
    lastUrgentId: z.string().min(1).nullable().optional(),
  })
  .strict();

export const saveGameSchema = z
  .object({
    saveVersion: z.literal(SAVE_VERSION),
    player: playerSchema,
    agency: agencySchema,
    clientTrust: z.record(z.string().min(1), z.number().int().min(0).max(100)),
    unlockedClients: z.array(z.string().min(1)),
    streak: streakSchema,
    offers: offerBoardSchema,
    lastSaveTime: nonNegativeInt,
    resultHistory: z.array(resultHistoryEntrySchema),
    training: trainingSchema,
  })
  .strict();
